import { useErrorReporter } from '#imports';
import {
    CardRevealComplete,
    Complete,
    Setup,
    StepBoxReveal,
    StepCardsReveal,
    StepIdle,
    StepPackReveal,
    UpdateCard,
    UpdateGoldenTicket,
    UpdateStep,
    MessageSchema,
    PartialUpdateCardSchema,
    PartialUpdateStepSchema,
} from '@fanatics/resn-api/instant-rips';
import { defineComponent, toRaw } from 'vue';

import { gsap } from '@resn/gsap';

import { useFanatics } from './FanaticsProvider';
import { useWindowMessage } from '~/composables/useWindowMessage';
import { CARDS_DUR, COMPLETE_DUR, PACK_DUR } from '~/core/constants';
import { useColorPalette } from '~/providers/ColorPaletteProvider';
import { createLogger } from '~/utils/logger';
import { extractDataPayload, normalizePackData } from '~/utils/normalize-payload';

const logger = createLogger('instant-rips.state');

const logValidationWarning = (message, messageType, errors, payload) => {
    logger.warn(message, {
        event: 'instant-rips.validation_warning',
        messageType,
        source: 'instant-rips/state-manager',
        reason: 'zod-error',
        errors,
        payload,
    });
};

const StateManager = defineComponent({
    setup() {
        const fanatics = useFanatics();
        const reportError = useErrorReporter();

        const { sendMessage } = useWindowMessage();
        const { setColorPaletteIndex } = useColorPalette();

        let isClientMode = false;

        useWindowMessage({
            onReceiveMessage: (e) => {
                if (!e.data.type) {
                    //console.warn('StateManager: Window message object does not have a type:', e);
                    return;
                }

                // Debug: log raw message structure to help identify iOS issues
                logger.debug('StateManager: Received message payload', e.data);

                const { type, clientMode } = e.data;
                const rawData = extractDataPayload(e.data);
                const data = normalizePackData(rawData);

                const shouldReset = fanatics.step.value !== StepIdle;

                isClientMode = clientMode;
                resetDelay?.kill();

                switch (type) {
                    case Setup: {
                        const validation = MessageSchema.safeParse(data);
                        if (!validation.success) {
                            logValidationWarning(
                                'StateManager: Setup payload validation warning',
                                Setup,
                                validation.error.format(),
                                data
                            );
                        }

                        if (shouldReset) {
                            resetAndSetup(data);

                            return;
                        }
                        fanatics.setup(data);
                        if (data.rerip) startReripFlow();
                        break;
                    }
                    case Complete:
                        if (!data) {
                            reportError(new Error('Invalid message structure: missing data payload'), {
                                source: 'instant-rips/state-manager',
                                kind: 'data-integrity',
                                reason: 'missing-data-payload',
                                messageType: type,
                                rawMessage: JSON.stringify(e.data).substring(0, 500),
                            });
                            return;
                        }
                        fanatics.complete();
                        if (shouldReset) {
                            resetAndSetup(data);
                            return;
                        }
                        break;
                    case UpdateStep: {
                        const validation = PartialUpdateStepSchema.safeParse(data);
                        if (!validation.success) {
                            logValidationWarning(
                                'StateManager: UpdateStep payload validation warning',
                                UpdateStep,
                                validation.error.format(),
                                data
                            );
                        }
                        // Fall through to shared logic
                    }
                    case UpdateCard: {
                        const validation = PartialUpdateCardSchema.safeParse(data);
                        if (!validation.success) {
                            logValidationWarning(
                                'StateManager: UpdateCard payload validation warning',
                                UpdateCard,
                                validation.error.format(),
                                data
                            );
                        }
                        // Fall through to shared logic
                    }
                    case UpdateGoldenTicket: {
                        if (!data) {
                            reportError(new Error('Missing payload for update message'), {
                                source: 'instant-rips/state-manager',
                                kind: 'data-integrity',
                                reason: 'missing-payload',
                                payload: e.data,
                            });
                            return;
                        }

                        fanatics.updateBreakData(data);

                        if (!data.breakInfo) {
                            reportError(new Error('Missing breakInfo in update payload'), {
                                source: 'instant-rips/state-manager',
                                kind: 'data-integrity',
                                reason: 'missing-breakinfo',
                                payload: e.data,
                            });
                            return;
                        }

                        fanatics.updateBreakGoldenTicket(data.breakInfo);

                        const cards = data.breakInfo?.box?.pack?.cards;
                        if (!Array.isArray(cards)) {
                            reportError(new Error('Missing cards array in break info'), {
                                source: 'instant-rips/state-manager',
                                kind: 'data-integrity',
                                reason: 'missing-breakinfo',
                                payload: e.data,
                            });
                            return;
                        }

                        fanatics.updateBreakCard(data.breakInfo);
                        break;
                    }
                    default:
                        //console.warn('StateManager: Unknown message type:', type);
                        break;
                }

                // NOTE: used for testing client-mode only
                const breakData = toRaw(fanatics.breakData.value);
                if (isClientMode) sendMessage('UPDATE_CLIENT_MODE', breakData);
            },
        });

        let resetDelay;
        let tlRerip;
        const resetAndSetup = (data) => {
            if (isClientMode) {
                const newBox = data.breakInfo.box;
                const breakData = toRaw(fanatics.breakData.value);
                breakData.breakInfo.box = newBox;
                breakData.id = data.id;

                sendMessage('UPDATE_CLIENT_MODE', breakData);
            }

            tlRerip?.kill();

            fanatics.updateBreakStep('');
            fanatics.complete();

            resetDelay?.kill();
            resetDelay = gsap.delayedCall(COMPLETE_DUR, () => {
                fanatics.updateBreakStep(StepIdle);
                setColorPaletteIndex(0);

                requestAnimationFrame(() => {
                    fanatics.setup(data);

                    // NOTE: used for testing client-mode only
                    const breakData = toRaw(fanatics.breakData.value);
                    if (isClientMode) sendMessage('UPDATE_CLIENT_MODE', breakData);
                });
            });
        };

        const startReripFlow = () => {
            const { cards, breakData } = fanatics;
            const breakInfo = breakData.value.breakInfo;

            let cardIndex = 0;

            const updateCard = (i, cardUpdateData) => {
                const breakDataClone = structuredClone(breakData.value);
                const card = breakDataClone.breakInfo.box.pack.cards[i];
                if (card) {
                    card.updateCardData = {
                        ...card.updateCardData,
                        ...cardUpdateData,
                    };
                }
                fanatics.updateBreakData(breakDataClone).updateBreakCard(breakDataClone.breakInfo);
            };

            // Recursive function to reveal each card automatically
            const presentNextCard = () => {
                const c = breakInfo.box.pack.cards[cardIndex]; // card current

                if (c) {
                    breakInfo.currentCardIndex = cardIndex;

                    updateCard(cardIndex, { active: true, revealed: false });

                    gsap.delayedCall(1, () => {
                        updateCard(cardIndex, { revealed: true });

                        fanatics.events.once(CardRevealComplete, () => {
                            if (cardIndex < cards.value.length - 1) {
                                cardIndex++;
                                presentNextCard();
                            }
                        });
                    });
                }
            };

            const presentStep = (step) => {
                breakData.value.breakInfo.currentStep = step;
                fanatics.updateBreakStep(step);
            };

            tlRerip = gsap
                .timeline()
                .add(() => presentStep(StepIdle))
                .add(() => presentStep(StepBoxReveal), `+=0.5`)
                .add(() => presentStep(StepPackReveal), `+=${PACK_DUR}`)
                .add(() => presentStep(StepCardsReveal), `+=${CARDS_DUR}`)
                .addLabel('cardReveal', '+=1.5')
                .add(presentNextCard, '+=1.5');
        };
    },

    render() {
        if (this.$slots.default) {
            return this.$slots.default();
        }
        return null;
    },
});

export default StateManager;
